import { useEffect, useMemo, useState } from 'react';
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.jsx';

// AiPerformance — A-1 expert review + deflection metrics (2026-08-31).
// The trust ramp needs an accuracy record before anyone hands the AI more
// autonomy. This page is where that record gets built: every AI triage
// session lands here for an expert to grade, and the headline numbers show
// what the AI is actually deflecting.

const WINDOW = 100; // sessions loaded (newest first) — the working review window

function outcomeBadge(s) {
  if (s.outcome === 'resolved') return { label: 'Resolved by AI', bg: '#dcfce7', fg: '#166534' };
  if (s.outcome === 'escalated') return { label: 'Escalated', bg: '#fef3c7', fg: '#92400e' };
  return { label: 'Open / abandoned', bg: '#f1f5f9', fg: '#475569' };
}

// Strip the [CONTINUE]/[READY]/... control tag the server leaves on stored turns.
function cleanTurn(text) {
  return String(text || '').replace(/\n?\[(CONTINUE|READY|ESCALATE|RESOLVED)\]\s*$/i, '');
}

export default function AiPerformance() {
  const { claims } = useAuth();
  const vendorId = claims?.vendor_id;

  const [sessions, setSessions] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!vendorId) return;
    return onSnapshot(
      query(
        collection(db, 'vendors', vendorId, 'aiTriageSessions'),
        orderBy('createdAt', 'desc'),
        limit(WINDOW),
      ),
      (s) => setSessions(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => setError(err.message),
    );
  }, [vendorId]);

  const stats = useMemo(() => {
    const list = sessions || [];
    const resolved = list.filter((s) => s.outcome === 'resolved').length;
    const escalated = list.filter((s) => s.outcome === 'escalated').length;
    const finished = resolved + escalated;
    const reviewed = list.filter((s) => s.review?.verdict).length;
    const good = list.filter((s) => s.review?.verdict === 'good').length;
    return {
      total: list.length,
      resolved,
      escalated,
      finished,
      deflection: finished > 0 ? Math.round((resolved / finished) * 100) : null,
      reviewed,
      agreement: reviewed > 0 ? Math.round((good / reviewed) * 100) : null,
    };
  }, [sessions]);

  return (
    <>
      <h2 style={{ margin: 0 }}>AI performance</h2>
      <p style={{ color: 'var(--muted)', marginTop: 4, maxWidth: 780 }}>
        Every AI support conversation ends up here. Review them — was the advice right, did it
        escalate at the right moment? Your ratings build the accuracy record that justifies
        trusting the AI with more of your first-line support.
      </p>
      {error && <div className="error">{error}</div>}

      {/* ---- Headline numbers ---- */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '10px 0 18px' }}>
        <Tile label="Sessions" value={stats.total} />
        <Tile label="Resolved by AI" value={stats.resolved} accent="#166534" />
        <Tile label="Escalated to a human" value={stats.escalated} />
        <Tile
          label="Deflection rate"
          value={stats.deflection === null ? '—' : `${stats.deflection}%`}
          accent="#166534"
          hint="of finished sessions the AI closed without dispatching anyone"
        />
        <Tile label="Reviewed by you" value={`${stats.reviewed}/${stats.total}`} />
        <Tile
          label="Expert agreement"
          value={stats.agreement === null ? '—' : `${stats.agreement}%`}
          accent={stats.agreement !== null && stats.agreement < 70 ? '#b91c1c' : '#166534'}
          hint="reviewed sessions rated good"
        />
      </div>

      {/* ---- Session list ---- */}
      {sessions === null && <div className="placeholder">Loading…</div>}
      {sessions && sessions.length === 0 && (
        <div className="placeholder">
          No AI conversations yet. They'll appear here as soon as a customer talks to the
          assistant before submitting a ticket.
        </div>
      )}
      {sessions && sessions.map((s) => <SessionCard key={s.id} s={s} />)}
    </>
  );
}

function Tile({ label, value, accent, hint }) {
  return (
    <div className="card" style={{ padding: '12px 16px', minWidth: 150 }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: accent || 'inherit' }}>{value}</div>
      <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>{label}</div>
      {hint && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2, maxWidth: 170 }}>{hint}</div>}
    </div>
  );
}

function SessionCard({ s }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const badge = outcomeBadge(s);
  const turns = Array.isArray(s.messages) ? s.messages : [];

  async function rate(verdict) {
    setBusy(true); setErr(null);
    try {
      await httpsCallable(functions, 'rateAiSession')({ sessionId: s.id, verdict, note });
      setNote('');
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  return (
    <div className="card" style={{ padding: 16, marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <strong>{s.skillName || 'Unknown skill'}</strong>
          <span style={{
            marginLeft: 8, fontSize: 12, padding: '2px 8px', borderRadius: 99,
            background: badge.bg, color: badge.fg,
          }}>{badge.label}</span>
          {s.review?.verdict && (
            <span style={{
              marginLeft: 6, fontSize: 12, padding: '2px 8px', borderRadius: 99,
              background: s.review.verdict === 'good' ? '#dcfce7' : '#fee2e2',
              color: s.review.verdict === 'good' ? '#166534' : '#b91c1c',
            }}>
              {s.review.verdict === 'good' ? '✓ rated good' : '✗ needs work'}
            </span>
          )}
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>
            {s.createdAt?.toDate?.().toLocaleString() || ''}
            {' · '}{turns.filter((m) => m.role === 'user').length} customer turns
            {Array.isArray(s.groundedIn) && s.groundedIn.length > 0 && (
              <> · grounded in {s.groundedIn.join(', ')}</>
            )}
          </div>
          <div style={{ fontSize: 14, marginTop: 6 }}>{s.initialDescription}</div>
        </div>
      </div>

      <button className="btn-chip" style={{ marginTop: 10 }} onClick={() => setOpen(!open)}>
        {open ? 'Hide' : 'Show'} conversation ({turns.length} messages)
      </button>

      {open && (
        <div style={{ marginTop: 10 }}>
          {turns.map((m, i) => (
            <div
              key={i}
              style={{
                padding: '8px 12px', borderRadius: 8, marginBottom: 6, fontSize: 13.5,
                background: m.role === 'user' ? '#eef2ff' : '#f8fafc',
                border: '1px solid var(--border)',
                whiteSpace: 'pre-wrap',
              }}
            >
              <strong style={{ fontSize: 11.5, color: 'var(--muted)', display: 'block' }}>
                {m.role === 'user' ? 'Customer' : 'AI'}
              </strong>
              {cleanTurn(m.text)}
            </div>
          ))}
          {s.review?.note && (
            <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
              <strong>Reviewer note{s.review.byName ? ` (${s.review.byName})` : ''}:</strong> {s.review.note}
            </div>
          )}
        </div>
      )}

      {/* Rating row — only meaningful once the session finished */}
      {(s.outcome === 'resolved' || s.outcome === 'escalated') && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <button className="btn-primary" disabled={busy} onClick={() => rate('good')}>
            {busy ? '…' : '✓ Good'}
          </button>
          <button className="btn-ghost-danger" disabled={busy} onClick={() => rate('needs_work')}>
            {'✗ Needs work'}
          </button>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note — what should it have done?"
            style={{ flex: 1, minWidth: 220 }}
            maxLength={2000}
          />
        </div>
      )}
      {err && <div className="error">{err}</div>}
    </div>
  );
}
